import { Button, IconClose, IconEdit, TextField, Tooltip, UserSelect } from 'uikit';
import { runOnRefExists } from 'utils/runOnRefExists';

import cn from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Styles from './ExternalFormMultiselect.module.css';

interface IModalMultiselectValue {
    item: any;
    handleRemove?: (e: React.SyntheticEvent) => void;
}

const ModalMultiselectValue: React.FC<IModalMultiselectValue> = (props) => {
    const { item, handleRemove } = props;
    const { id, disabled, label } = item;

    return (
        <div key={id} className={Styles.externalFormMultiselectValue}>
            <span title={label}>{label}</span>
            {!disabled && (
                <IconClose
                    onClick={disabled ? undefined : handleRemove}
                    size="xs"
                    style={{ flex: '0 0 auto' }}
                    className={
                        disabled
                            ? Styles.externalFormMultiselectValueRemoveDisabled
                            : Styles.externalFormMultiselectValueRemove
                    }
                />
            )}
        </div>
    );
};

ModalMultiselectValue.displayName = 'ModalMultiselectValue';

const HEIGHT_INPUT = 33;

export interface IExternalFormMultiselectItem {
    id: number;
    label: string;
    data: any;
}

interface IExternalFormMultiselect {
    selectedItems: IExternalFormMultiselectItem[];
    onChange: (selectedItems: IExternalFormMultiselectItem[]) => void;
    onSearchFormOpen: () => void;
    label?: string;
    labelPosition?: 'left' | 'top';
    refCheckBtn?: React.MutableRefObject<null>;
    disabled?: boolean;
    status?: 'alert' | 'warning' | 'success' | undefined;
    disabledIds?: number[];
    className?: string;
    tooltipText?: string;
    countTitleType?: string;
    wrapperClassName?: string;
}

export const ExternalFormMultiselect: React.FC<IExternalFormMultiselect> = (props) => {
    const {
        onChange,
        selectedItems,
        children,
        onSearchFormOpen,
        label,
        labelPosition,
        refCheckBtn,
        disabled,
        status,
        disabledIds,
        className,
        tooltipText,
        countTitleType,
        wrapperClassName,
    } = props;
    const [isOpenTooltip, setIsOpenTooltip] = useState<boolean>(false);
    const inputRef = useRef<HTMLDivElement>(null);
    const selectInputRef = useRef<HTMLInputElement>(null);

    const items = useMemo(
        () => selectedItems.map((i) => ({ ...i, disabled: disabledIds?.includes(i.id) || disabled })),
        [disabled, disabledIds, selectedItems],
    );

    const isShowClearButton = useMemo(() => {
        const hasDisabledTrue = items.some((item) => item.disabled);
        const hasDisabledFalse = items.some((item) => !item.disabled);

        return hasDisabledTrue && !hasDisabledFalse;
    }, [items]);

    const onLocalChange = useCallback(
        ({ value }) => {
            if (!value?.length && disabledIds?.length) {
                onChange(items.filter((i) => i.disabled));
            } else {
                onChange(value);
            }
        },
        [disabledIds?.length, items, onChange],
    );

    const handleMouseMoveButton = useCallback(() => {
        setIsOpenTooltip(true);
    }, []);

    const handleMouseLeaveButton = useCallback(() => {
        setIsOpenTooltip(false);
    }, []);

    useEffect(() => {
        runOnRefExists(selectInputRef, () => {
            if (selectInputRef.current) {
                selectInputRef.current.disabled = true;
            }
        });
    }, [disabled]);

    const valueTextField = items[0]?.label ?? '';

    return (
        <>
            <div
                className={cn(Styles.externalFormMultiselectContainer, wrapperClassName)}
                ref={inputRef}
                onMouseMove={handleMouseMoveButton}
                onMouseLeave={handleMouseLeaveButton}
            >
                {disabled ? (
                    <TextField
                        value={valueTextField}
                        labelPosition="left"
                        width="full"
                        size="s"
                        disabled
                        label={label}
                        className={Styles.textField}
                    />
                ) : (
                    <UserSelect
                        disabled={disabled}
                        labelPosition={labelPosition}
                        label={label}
                        multiple
                        onChange={onLocalChange}
                        items={items}
                        value={items.length ? items : null}
                        className={cn(Styles.modalMultiselect, className, {
                            [Styles.one_row]: inputRef?.current?.clientHeight === HEIGHT_INPUT,
                            [Styles.disabled_select]: disabled,
                            [Styles.isShowClearButton]: isShowClearButton,
                        })}
                        dropdownClassName={Styles.hiddenDropdown}
                        renderValue={ModalMultiselectValue}
                        size="s"
                        status={status}
                        inputRef={selectInputRef}
                        placeholder={disabled ? '' : 'Выберите'}
                    />
                )}
                {!disabled && !tooltipText && (
                    <div
                        className={Styles.externalFormMultiselectRightSide}
                        style={status === 'alert' ? { borderColor: 'var(--color-bg-alert)' } : undefined}
                    >
                        <Button
                            type="button"
                            disabled={disabled}
                            label="Выбрать"
                            iconRight={IconEdit}
                            view="clear"
                            onlyIcon
                            size="xs"
                            onClick={onSearchFormOpen}
                            className={cn(Styles.externalFormMultiselectOpenButton, {
                                [Styles.disabled_btn]: disabled,
                            })}
                            ref={refCheckBtn}
                        />
                    </div>
                )}
            </div>
            {isOpenTooltip && tooltipText && <Tooltip anchorRef={inputRef}>{tooltipText}</Tooltip>}
            <div>{children}</div>
            {!disabled && (
                <p className={Styles.countItemText}>
                    Выбрано {countTitleType || ''}: {items.length}
                </p>
            )}
        </>
    );
};

ExternalFormMultiselect.displayName = 'ModalMultiselect';
